import { useEffect, useRef, useState } from 'react';

import $query from 'app/stores/query/$query';
import makeCancelable, { CanceledError } from 'core/util/cancelablePromise';
import { calculateSampleRate } from '@kentik/ui-shared/util/sampleRate';
import {
  FlowMeasurement,
  PlanFlowSelectionStrategy,
  deduplicateReceivedFlowResults,
  getAvailableReceivedMeasurements,
  getFlowQueries,
  sourceHasData
} from '../deviceFlowMetricsQueryUtils';

/**
 * @typedef {Object} Filter
 * @property {string} filterField
 * @property {string} operator
 * @property {string|number} filterValue
 */

/**
 * @typedef {Object} PlanFlowRateResult
 * @property {number} receivedFps - Plan-level received flow rate (fps).
 * @property {number} storedFps - Plan-level stored flow rate (fps).
 * @property {number} flowSampleRate
 * @property {boolean} hasFlowData - True if any query returned at least one model with flow data
 * @property {boolean} loadingPlanFlow
 * @property {boolean} planFlowError
 * @property {Array<[number, number]>} [receivedFlowTimeseries]
 * @property {Array<[number, number]>} [storedFlowTimeseries]
 * @property {Object<string, object|null>} [receivedFlowResultsByMeasurement]
 * @property {object|null} [storedFlowData]
 */

/**
 * Hook to fetch and calculate plan flow rate metrics.
 * Automatically skips data fetching until plan ID is available.
 *
 * @param {Object} options - Configuration object
 * @param {Filter[]} [options.filters] - Query filters; if not provided, plan ID filter is auto-generated
 * @param {number} [options.limit=1000] - Maximum number of results to return
 * @param {string} [options.lookback='PT86400S'] - Time lookback period for flow queries (ISO 8601 duration)
 * @param {string|number} options.planId - The plan ID to fetch flow data for
 * @param {string} [options.rollup='p95'] - Rollup function to apply (p95, avg, max, etc.)
 * @param {PlanFlowSelectionStrategy} [options.selectionStrategy=PlanFlowSelectionStrategy.HIGHEST_FPS] - Measurement set + aggregation mode
 * @param {boolean} [options.withRawData=false] - If true, includes raw flow data and timeseries in returned object
 *
 * @returns {PlanFlowRateResult} Flow metrics and status object
 */
function usePlanFlowRate({
  filters,
  limit = 1000,
  lookback = 'PT86400S',
  planId,
  rollup = 'p95',
  selectionStrategy = PlanFlowSelectionStrategy.HIGHEST_FPS,
  withRawData = false
}) {
  const [receivedFlowResultsByMeasurement, setReceivedFlowResultsByMeasurement] = useState({});
  const [storedFlowData, setStoredFlowData] = useState(null);
  const [loadingPlanFlow, setLoadingPlanFlow] = useState(false);
  const [planFlowError, setPlanFlowError] = useState(false);

  // Use ref outside of state to track fetch state and prevent unnecessary re-fetches
  const isFetchingRef = useRef(false);

  // Default to `plan_id` filter if no filters provided
  const effectiveFilters = filters ?? (planId ? [{ filterField: 'plan_id', operator: '=', filterValue: planId }] : []);

  const isSaasOnly = selectionStrategy === PlanFlowSelectionStrategy.SAAS_ONLY;

  useEffect(() => {
    // Skip if plan hasn't loaded yet
    if (!planId || isFetchingRef.current) {
      return () => {};
    }

    isFetchingRef.current = true;
    let isCanceled = false;
    let flowRequest;

    const receivedMeasurements = isSaasOnly ? [FlowMeasurement.FLOW] : getAvailableReceivedMeasurements();
    const flowDimensions = isSaasOnly ? ['plan_id'] : ['device_id', 'plan_id'];
    const flowMerge = isSaasOnly ? { dimensions: ['device_id'], aggregate: 'sum' } : null;
    const flowTimeseriesLimit = isSaasOnly ? 1 : 0;

    // Pair each measurement with its query so filtering never breaks the index alignment needed
    // when mapping results back to measurements after Promise.all resolves.
    const receivedMeasurementQueries = receivedMeasurements
      .map((measurement) => ({
        measurement,
        query: getFlowQueries({
          dimensions: flowDimensions,
          filters: effectiveFilters,
          limit,
          lookback,
          metric: 'in',
          merge: flowMerge,
          rollup,
          timeseriesLimit: flowTimeseriesLimit,
          measurement
        })
      }))
      .filter(({ query }) => query != null);

    // Build query for stored (outbound) flow metrics; all devices use FLOW measurement for stored flow
    const storedFlowQuery = getFlowQueries({
      dimensions: flowDimensions,
      filters: effectiveFilters,
      limit,
      lookback,
      metric: 'out',
      merge: flowMerge,
      rollup,
      timeseriesLimit: flowTimeseriesLimit,
      measurement: FlowMeasurement.FLOW
    });

    const fetchFlowData = async () => {
      setLoadingPlanFlow(true);

      const receivedQueryPromises = receivedMeasurementQueries.map(({ query }) => $query.runQuery(query));

      flowRequest = makeCancelable(Promise.all([...receivedQueryPromises, $query.runQuery(storedFlowQuery)]));

      try {
        const results = await flowRequest.promise;
        const storedFlowResults = results[results.length - 1];
        const receivedResults = results.slice(0, -1);

        const rawResultsByMeasurement = receivedMeasurementQueries.reduce((acc, { measurement }, index) => {
          acc[measurement] = receivedResults[index]?.results;
          return acc;
        }, {});

        // 'highestFps' deduplicates per-device across measurements, attributing each device to the source with the highest received-flow value.
        // 'saasOnly' has a single FLOW source already merged to one plan row, so no deduplication is needed.
        const resultsByMeasurement = isSaasOnly
          ? rawResultsByMeasurement
          : deduplicateReceivedFlowResults(rawResultsByMeasurement, { rollup, metric: 'in' });

        if (!isCanceled) {
          setPlanFlowError(false);
          setReceivedFlowResultsByMeasurement(resultsByMeasurement);
          setStoredFlowData(storedFlowResults?.results);
        }
      } catch (error) {
        if (error instanceof CanceledError) {
          isCanceled = true;
          return;
        }

        // Clear data states on error to prevent stale data
        if (!isCanceled) {
          setReceivedFlowResultsByMeasurement({});
          setStoredFlowData(null);
          setPlanFlowError(true);
        }
        console.error('Error fetching plan flow data: ', error);
      } finally {
        if (!isCanceled) {
          setLoadingPlanFlow(false);
        }
        isFetchingRef.current = false;
      }
    };

    // Invoke the data-fetching function defined inside this useEffect to allow async/await usage and proper handling of isCanceled flag.
    fetchFlowData();

    return () => {
      isCanceled = true;
      isFetchingRef.current = false;

      if (flowRequest) {
        flowRequest.cancel();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, lookback, selectionStrategy]);

  const receivedSources = Object.values(receivedFlowResultsByMeasurement).filter(Boolean);

  const hasFlowData = receivedSources.some(sourceHasData) || sourceHasData(storedFlowData);

  const receivedModel = isSaasOnly ? receivedSources[0]?.models[0] : undefined;
  const storedModel = isSaasOnly ? storedFlowData?.models[0] : undefined;

  const receivedFps = receivedModel?.get(`${rollup}_in`) || 0;
  const storedFps = storedModel?.get(`${rollup}_out`) || 0;
  const flowSampleRate = calculateSampleRate(receivedFps, storedFps);

  const receivedFlowTimeseries = receivedModel?.get('timeseries') ?? [];
  const storedFlowTimeseries = storedModel?.get('timeseries') ?? [];

  // Return basic metrics by default, or include raw data if requested
  const basicReturn = {
    receivedFps,
    storedFps,
    planFlowError,
    flowSampleRate,
    hasFlowData,
    loadingPlanFlow
  };

  return withRawData
    ? {
        ...basicReturn,
        receivedFlowTimeseries,
        storedFlowTimeseries,
        receivedFlowResultsByMeasurement,
        storedFlowData
      }
    : basicReturn;
}

export default usePlanFlowRate;
